import { useState, useEffect } from 'react';

/**
 * Custom hook that persists state in localStorage
 * @param key - The localStorage key to use
 * @param initialValue - The initial value if nothing is in localStorage
 * @returns A stateful value and a function to update it, just like useState
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void] {
  // Get initial value from localStorage or use provided initial value
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Update localStorage when state changes
  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(storedValue));
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, storedValue]);

  // Return a wrapped version of useState's setter function that also updates localStorage
  const setValue = (value: T | ((val: T) => T)) => {
    setStoredValue(value);
  };

  return [storedValue, setValue];
}

/**
 * Simpler version for boolean values with a default of false
 */
export function useLocalStorageBoolean(key: string, defaultValue = false) {
  return useLocalStorage(key, defaultValue);
}

/**
 * Simpler version for string values
 */
export function useLocalStorageString(key: string, defaultValue = '') {
  return useLocalStorage(key, defaultValue);
}

/**
 * Simpler version for number values
 */
export function useLocalStorageNumber(key: string, defaultValue = 0) {
  return useLocalStorage(key, defaultValue);
}